// © 2015-2021 WDNA - All Rights Reserved

import * as React from 'react';
import _ from 'lodash';

import { RowDataUpdatedEvent } from 'wdna-grid-table/lib/types';
import Table, { TableButtonsConfig } from 'wdna-components/lib/table/Table';
import { TabConfig, addTab, removeTab } from 'wdna-components/lib/tabs';
import { messageInfo, messageError, messageWarning, messageBoxText } from 'wdna-components/lib/helpers/notification';
import { getErrorMessage } from 'wdna-components/lib/helpers/response';
import { hasLevel } from 'wdna-components/lib/helpers/permission-new';

import AdjustmentsEdit from './Edit';

import { sensorAdjustment, SensorAdjustmentDO } from '../../../requests';

interface Props {
  rowId: number;
}

interface State {
  loading: boolean;
  update: string;
  showGrid: boolean;

  data: SensorAdjustmentDO[];
}

export default class List extends React.Component<Props, State> {
  private pid: string;
  private mounted: boolean;
  private buttons: TableButtonsConfig;

  public constructor(props: Props) {
    super(props);

    this.pid = window.getStr();
    this.mounted = false;

    this.buttons = {
      add: hasLevel('role', ['ADM', 'URW']),
      del: hasLevel('role', ['ADM', 'URW']),
      edit: true,
    };

    this.state = {
      loading: false,
      update: window.getStr(),
      showGrid: false,

      data: [],
    };
  }

  public componentDidMount(): void {
    this.mounted = true;
    this.load();
  }

  public shouldComponentUpdate(nextProps: Props, nextState: State): boolean {
    return this.state.update !== nextState.update;
  }

  public componentWillUnmount(): void {
    this.mounted = false;
  }

  public setLocalState = (nextState: Partial<State>, callback?: (() => void)): void => {
    if (this.mounted) {
      this.setState((prevState: State) => ({
        ...prevState,
        ...nextState,
      }), callback);
    }
  };

  public load = (): void => {
    const { rowId } = this.props;

    this.loadingStart();
    sensorAdjustment<SensorAdjustmentDO[]>('list', undefined, { sensorId: rowId })
      ?.then((resp: SensorAdjustmentDO[]) => {
        this.setLocalState({ data: resp });
      })
      .catch((error: any) => {
        messageError('Error', getErrorMessage(error));
      })
      .finally(() => {
        this.loadingFinish();
      });
  };

  public loadingStart = (): void => {
    this.setLocalState({
      update: window.getStr(),
      loading: true,
    });
  };

  public loadingFinish = (): void => {
    this.setLocalState({
      update: window.getStr(),
      loading: false,
    });
  };

  public loadTab = (rowId?: number): void => {
    const { rowId: sensorId } = this.props;
    const tabId: string = 'ADJE';

    window.store.dispatch(removeTab(tabId));
    const tabData: TabConfig = {
      id: tabId,
      active: true,
      title: `${rowId ? `Adjustment Edit: ${rowId}` : 'Adjustment New'} | Sensor ID: ${sensorId}`,
      component: AdjustmentsEdit,
      props: {
        rowId,
        tabId: this.pid,
        report: 'ADJUSTMENT EDIT',
        title: 'Adjustment Edit',
        notify: this.notify,
        sensorId,
      },
    };
    window.store.dispatch(addTab(tabData));
  };

  public notify = (): void => {
    this.load();
  };

  public onEdit = (item: SensorAdjustmentDO): void => {
    const rowId: number = item.id;
    setTimeout(() => {
      this.loadTab(rowId);
    }, 0);
  };

  public onDelete = (item: SensorAdjustmentDO): void => {
    const confirm: string = String(item.id);
    messageBoxText('Delete', `Enter '${confirm}' to confirm you want to delete the item: ${item.id}?`, (value?: string) => {
      if (confirm === value) {
        this.loadingStart();
        sensorAdjustment('del', String(item.id))
          ?.then(() => {
            messageInfo('Success', 'Register successfully deleted', 5000);
            this.load();
          })
          .catch((error: any) => {
            messageError('Error', getErrorMessage(error));
          })
          .finally(() => {
            this.loadingFinish();
          });
      } else {
        messageWarning('Warning', 'Wrong input parameter', 5000);
      }
    }, 'remove-circle', undefined, confirm);
  };

  public rowDataUpdated = (_event: RowDataUpdatedEvent): void => {
    this.setLocalState({
      update: window.getStr(),
      showGrid: true,
    });
  };

  public render(): JSX.Element {
    const { loading, data, showGrid } = this.state;

    const columnAlias: Record<string, string> = {
      id: 'ID',
      sensorName: 'Sensor',
      typeSensorName: 'Sensor type',
      variableName: 'Variable',
      formula: 'Formula',
      comments: 'Comments',
    };
    const columnsToHide: string[] = [
      'sensorId',
      'variableId',
      'typeSensorVariableId',
      'typeSensorVariableFormula',
    ];
    const columnStyles: Record<string, any> = {
      id: { textAlign: 'right' },
      sensorName: { textAlign: 'left' },
      typeSensorName: { textAlign: 'left' },
      variableName: { textAlign: 'left' },
      formula: { textAlign: 'left' },
      comments: { textAlign: 'left' },
      actionButtons: { textAlign: 'center' },
    };

    return (
      <div id="content" className="content" style={{ visibility: (showGrid ? 'visible' : 'hidden') }}>
        <Table
          data={data || []}
          alias={columnAlias}
          customCellStyles={columnStyles}
          hiddenCols={columnsToHide}
          buttons={this.buttons}
          onEdit={this.onEdit}
          onDelete={this.onDelete}
          onAdd={this.onEdit}
          loading={loading}
          rowDataUpdated={this.rowDataUpdated}
        />
      </div>
    );
  }
}
